import React, { useCallback, useContext } from 'react'
import moment from 'moment'
import { useIntl, FormattedMessage } from 'react-intl'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import OfferPropertyCancellationPolicyModal from './OfferPropertyCancellationPolicyModal'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  isFlashUSP?: boolean
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days'))
  let startDate: moment.Moment | undefined
  if (startBasedOnCheckIn) {
    startDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
  }

  const showModal = useContext(ModalContext)

  const onItemClick = useCallback(() => {
    showModal(<OfferPropertyCancellationPolicyModal startDate={startDate} checkInDate={checkInDate} />)
  }, [showModal, startDate, checkInDate])

  const freeCancellationLabel = intl.formatMessage({
    id: 'qUOBB5iD+F',
    defaultMessage: 'Free cancellation',
    description: 'Bold label for free cancellation policy in offer property cancellation',
  })

  const highlightText = useCallback((chunks: React.ReactNode) => (
    <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">{chunks}</BodyText>
  ), [])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick}>
      <BodyText variant="medium" as="span">
        {!startDate && <FormattedMessage
          id="jQTenrMzd2"
          defaultMessage="<highlight>Free cancellation</highlight> within 7 days of purchase (Hotel only)"
          description="Free cancellation policy within 7 days of purchase for hotel bookings"
          values={{ highlight: highlightText }}
        />}
        {startDate && <FormattedMessage
          id="c6nHzkcCVi"
          defaultMessage="<highlight>Free cancellation</highlight> until {date} (Hotel only)"
          description="Free cancellation policy with deadline date for hotel bookings"
          values={{
            highlight: highlightText,
            date: intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT),
          }}
        />}
      </BodyText>
      {' '}<MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      colour="highlight-secondary"
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      {freeCancellationLabel}
    </TextLink>}
  </>
}

export default OfferPropertyCancellationPolicies
